import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ClipboardList, FileEdit, LogOut, ArrowUpRight, ExternalLink, ShieldAlert } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

const tiles = [
  {
    to: "/admin/ops",
    title: "Administración",
    subtitle: "Solicitudes, pedidos, equipo y facturas",
    Icon: ClipboardList,
    testid: "hub-ops",
    accent: "from-[#D4AF37] to-[#B8941F]",
    bullets: ["Inbox de presupuestos", "Pedidos y estados", "Facturación con impuestos"],
  },
  {
    to: "/admin/cms",
    title: "Editar landing page",
    subtitle: "Textos, imágenes y visibilidad de cada sección",
    Icon: FileEdit,
    testid: "hub-cms",
    accent: "from-[#E6C75A] to-[#9C7A1F]",
    bullets: ["11 secciones editables", "Reordenar y ocultar items", "Marca y redes sociales"],
  },
];

export default function AdminHub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body relative overflow-hidden">
      <div className="gold-orb a" style={{ width: 400, height: 400, top: "-10%", left: "-5%" }} />
      <div className="gold-orb b" style={{ width: 300, height: 300, bottom: "-10%", right: "-10%" }} />

      <header className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-8 sm:pt-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo size={42} variant="mark" />
          <div className="leading-tight">
            <div className="logo-mark text-white text-base">M&amp;C</div>
            <div className="logo-mark text-[10px] tracking-[0.3em] text-[#D4AF37]">CLEAN FRESH</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View site
          </a>
          <button
            onClick={onLogout}
            data-testid="hub-logout"
            className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 pt-20 sm:pt-28 pb-20">
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-3">
          Welcome back
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
          ¿Qué quieres hacer <span className="text-gradient-gold">hoy?</span>
        </h1>
        <p className="mt-4 text-white/60 text-base sm:text-lg max-w-xl">
          Sesión iniciada como <span className="text-white">{user?.email}</span>. Elige una herramienta para continuar.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {tiles.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className="group relative overflow-hidden rounded-3xl bg-white/[0.04] border border-white/10 p-7 sm:p-9 hover:border-[#D4AF37]/50 hover:-translate-y-1 transition-all duration-500 hover:shadow-[0_30px_60px_-30px_rgba(212,175,55,0.6)]"
            >
              <div className="absolute -top-24 -right-16 w-64 h-64 rounded-full bg-[#D4AF37]/10 blur-[80px] group-hover:bg-[#D4AF37]/25 transition-all" />

              <div className="relative flex items-start justify-between mb-8">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${t.accent} flex items-center justify-center shadow-[0_12px_28px_-10px_rgba(212,175,55,0.5)]`}>
                  <t.Icon className="w-6 h-6 text-[#0A0A0A]" strokeWidth={2.2} />
                </div>
                <ArrowUpRight className="w-6 h-6 text-white/30 group-hover:text-[#D4AF37] group-hover:rotate-12 transition-all" />
              </div>

              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">{t.title}</h2>
              <p className="text-white/60 text-sm sm:text-base mb-6">{t.subtitle}</p>

              <ul className="space-y-2">
                {t.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-white/70">
                    <span className="w-1 h-1 rounded-full bg-[#D4AF37]" />
                    {b}
                  </li>
                ))}
              </ul>
            </Link>
          ))}
        </div>

        {user?.role === "super_admin" && (
          <div className="mt-6">
            <Link
              to="/admin/system"
              data-testid="hub-system"
              className="group block relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-500/10 to-[#0A0A0A] border border-red-500/30 p-6 sm:p-7 hover:border-red-400/60 transition-all"
            >
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-2xl bg-red-500/20 border border-red-500/40 flex items-center justify-center shrink-0">
                  <ShieldAlert className="w-5 h-5 text-red-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h2 className="font-display text-lg font-bold">Control de sistema</h2>
                    <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-red-500/15 text-red-300 border border-red-500/30 font-bold">
                      Super admin
                    </span>
                  </div>
                  <p className="text-white/60 text-sm">Activar / desactivar landing y panel admin a nivel sistema.</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-red-300 transition-colors" />
              </div>
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
